import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AlertCircle, Clock, ArrowRight } from 'lucide-react';

const InvitationPendingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Parse URL hash for parameters
    const hash = location.hash.substring(1); // Remove the #
    const params = new URLSearchParams(hash);
    
    // console.warn('InvitationPendingPage - URL hash:', hash);
    // console.warn('InvitationPendingPage - Parsed params:', Object.fromEntries(params));
    
    // Check if this is a fresh invitation (type=invite) or an error
    const type = params.get('type');
    const error = params.get('error');
    const errorCode = params.get('error_code');
    
    if (type === 'invite') {
      // This is a fresh invitation - redirect to password reset to complete the flow
      // console.warn('Fresh invitation detected, redirecting to password reset');
      navigate(`/password-reset${location.hash}`, { replace: true });
      return;
    } else if (!error && !errorCode) {
      // No error parameters - redirect to password reset as fallback
      // console.warn('No error parameters, redirecting to password reset');
      navigate(`/password-reset${location.hash}`, { replace: true });
      return;
    }
  }, [location, navigate]);

  // Parse error information for display
  const hash = location.hash.substring(1);
  const params = new URLSearchParams(hash);
  const errorCode = params.get('error_code');
  const errorDescription = params.get('error_description');

  // Show expired invitation page
  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900">
            <Clock className="h-6 w-6 text-orange-600 dark:text-orange-400" />
          </div>
          
          <CardTitle className="text-2xl">Invitation Link Expired</CardTitle>
          <CardDescription>
            The invitation link you clicked has expired or is no longer valid.
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {errorDescription && (
            <div className="rounded-md bg-orange-50 dark:bg-orange-900/20 p-3">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-orange-400 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-sm text-orange-800 dark:text-orange-300 font-medium">
                    {errorDescription}
                  </p>
                  {errorCode && (
                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                      Error code: {errorCode}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <h3 className="font-medium text-sm">What to do next:</h3>
            
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <span className="font-medium text-foreground">1.</span>
                <span>If you already set your password, try logging in directly</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-medium text-foreground">2.</span>
                <span>If you haven't set your password yet, contact your administrator for a new invitation</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-medium text-foreground">3.</span>
                <span>If you're having trouble, use the "Forgot Password" option on the login page</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-4">
            <Button asChild className="w-full">
              <a href="/login">
                <ArrowRight className="w-4 h-4 mr-2" />
                Go to Login
              </a>
            </Button>
            
            <Button variant="outline" asChild className="w-full">
              <a href="/request-password-reset">
                Reset Password
              </a>
            </Button>
          </div>

          <div className="text-center text-xs text-muted-foreground pt-2">
            Need help? Contact your system administrator.
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvitationPendingPage;